import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { FileText, Upload, Loader2, CheckCircle2, ExternalLink, Sparkles } from "lucide-react";

interface Props {
  companyId: string;
}

interface Template {
  id: string;
  storage_path: string;
  original_filename: string;
  file_size_bytes: number;
  detection: "fillable" | "non_fillable" | "invalid";
  field_count: number | null;
  uploaded_at: string;
}

const fmtSize = (b: number) =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;

export function BrokerCreditAppTemplateTab({ companyId }: Props) {
  const [current, setCurrent] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("broker_credit_app_templates")
      .select("id, storage_path, original_filename, file_size_bytes, detection, field_count, uploaded_at")
      .eq("company_id", companyId)
      .eq("is_active", true)
      .maybeSingle();
    if (error) {
      toast({ title: "Could not load template", description: error.message, variant: "destructive" });
    }
    setCurrent((data as Template | null) ?? null);
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleFile = async (file: File) => {
    if (!file.type.toLowerCase().includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      toast({ title: "Invalid file", description: "Please upload a PDF.", variant: "destructive" });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 15 MB.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("company_id", companyId);
      fd.append("file", file);
      const { data, error } = await supabase.functions.invoke("inspect-credit-app-pdf", { body: fd });

      // The edge function returns 400 for not_fillable / invalid_pdf; supabase-js
      // surfaces non-2xx as `error`, but the JSON body is still returned in `data`.
      const payload = (data ?? {}) as { ok?: boolean; reason?: string; error?: string; field_count?: number };

      if (payload?.reason === "not_fillable" || payload?.reason === "invalid_pdf") {
        setRejectOpen(true);
        return;
      }
      if (error) {
        toast({ title: "Upload failed", description: error.message, variant: "destructive" });
        return;
      }

      toast({
        title: "Template ready",
        description: `Detected ${payload?.field_count ?? 0} fillable form fields. AI will auto-fill it when you generate a credit app.`,
      });
      await load();
    } catch (e) {
      toast({ title: "Upload failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) void handleFile(f);
  };

  return (
    <>
      <Card className="p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Credit Application Template</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Upload your blank credit app as a <span className="font-medium text-foreground">fillable PDF</span>.
            When you win a load, one click will auto-fill it from the shipper's credit profile using AI —
            no field mapping, no configuration.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : current ? (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-start gap-3 min-w-0">
              <FileText className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">{current.original_filename}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {fmtSize(current.file_size_bytes)} · uploaded {new Date(current.uploaded_at).toLocaleString()}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Fillable PDF
                  </Badge>
                  {current.field_count != null && (
                    <span className="text-xs text-muted-foreground">{current.field_count} form fields</span>
                  )}
                  <Badge variant="secondary" className="text-[10px]">
                    <Sparkles className="h-3 w-3 mr-1" /> AI auto-fill ready
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No template uploaded yet.
          </div>
        )}

        <div>
          <label htmlFor="credit-app-upload">
            <input
              id="credit-app-upload"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              disabled={uploading}
              onChange={onPick}
            />
            <Button asChild disabled={uploading}>
              <span className="cursor-pointer">
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> {current ? "Replace template" : "Upload template"}</>
                )}
              </span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground mt-2">
            Fillable PDF only, max 15 MB. Flat/scanned PDFs will be rejected.
          </p>
        </div>
      </Card>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Please upload a fillable PDF</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Your credit app needs to be a <span className="font-medium text-foreground">fillable PDF</span> so
                  our AI can auto-complete it from each shipper's credit profile. The file you uploaded doesn't have
                  any fillable form fields.
                </p>
                <p>
                  You can convert your credit app to a fillable PDF using Adobe Acrobat's{" "}
                  <span className="font-medium">Prepare Form</span> tool or a similar service, then re-upload it here.
                </p>
                <div className="rounded-md border bg-muted/40 p-3 space-y-2">
                  <a
                    href="https://helpx.adobe.com/acrobat/using/creating-distributing-pdf-forms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-primary hover:underline text-sm font-medium"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    How to make a fillable PDF (Adobe Acrobat docs)
                  </a>
                  <div className="text-xs text-muted-foreground">
                    Need help? Contact us at{" "}
                    <a href="mailto:info@lanerunner.com" className="text-primary hover:underline">
                      info@lanerunner.com
                    </a>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Try again</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
